import { z } from 'zod';

import { MetaSchema, Rfc3339DateTime } from './create-chat.js';

/**
 * Shared Zod schemas + inferred types for the workstream entity and list
 * response — the Drop-5 Phase A data contract (ADR-0033, S-A2).
 *
 * One source of truth imported by both the BFF handlers (A3/B1/B2) and the
 * SPA client (C1) so the wire contract can't drift (frontend-architecture R9 /
 * security SR10).
 *
 * Attribute data classification: `title` is user-derived, business-sensitive —
 * do not log raw values anywhere in the stack (ADR-0033).
 */

/**
 * The workstream entity as returned on the wire (tolerant-out per ADR-0024 §11:
 * `z.looseObject` so future additive server fields survive client-side parsing).
 *
 * Mirrors the DynamoDB attribute set decided in ADR-0033:
 *   workstreamId  — stable UUID, minted at create
 *   tenantId      — Auth0 org id (immutable)
 *   userId        — Auth0 sub (immutable)
 *   title         — user-derived, business-sensitive; non-empty
 *   description   — optional free text
 *   createdAt     — RFC-3339 UTC (Z-suffixed), reusing Rfc3339DateTime
 */
export const WorkstreamSchema = z.looseObject({
  workstreamId: z.string().min(1),
  tenantId: z.string().min(1),
  userId: z.string().min(1),
  title: z.string().min(1),
  description: z.string().optional(),
  createdAt: Rfc3339DateTime,
});
export type Workstream = z.infer<typeof WorkstreamSchema>;

/**
 * The `data` array in the list response — an ordered array of workstream
 * entities (tolerant-out: `z.looseObject` on each element via WorkstreamSchema).
 * No upper bound enforced here; pagination is a future `meta` extension.
 */
export const ListWorkstreamsDataSchema = z.array(WorkstreamSchema);
export type ListWorkstreamsData = z.infer<typeof ListWorkstreamsDataSchema>;

/**
 * Full `{ data: Workstream[], meta }` list-response envelope (ADR-0024 §4).
 * Tolerant-out at the envelope level (`z.looseObject`) so future top-level
 * pagination / diagnostic fields survive the parse.
 */
export const ListWorkstreamsResponseSchema = z.looseObject({
  data: ListWorkstreamsDataSchema,
  meta: MetaSchema,
});
export type ListWorkstreamsResponse = z.infer<typeof ListWorkstreamsResponseSchema>;

/**
 * `data` of `DELETE /api/v1/workstreams/{id}` — the deleted workstream id
 * (idempotent, REST DELETE semantics; an unknown/unowned id never reaches here —
 * the BFF's ownership gate returns 404 first). A hard delete: the agent removes
 * the workstream's DynamoDB state (chat + artifact ledger) and every S3 artifact,
 * then the BFF deletes its own row. Mirrors `DeleteArtifactDataSchema`.
 */
export const DeleteWorkstreamDataSchema = z.strictObject({
  workstreamId: z.string().min(1),
});
export type DeleteWorkstreamData = z.infer<typeof DeleteWorkstreamDataSchema>;

export const DeleteWorkstreamResponseSchema = z.looseObject({
  data: DeleteWorkstreamDataSchema,
  meta: MetaSchema,
});
export type DeleteWorkstreamResponse = z.infer<typeof DeleteWorkstreamResponseSchema>;
